import * as Sentry from '@sentry/react';
import { isAxiosError } from 'axios';
import { isObject } from 'lodash-es';

import { API_BASE_URL } from '~constants/consts';

import { ignoreCommandAlkonConnection404Rule } from './ignore-rules/ignore-command-alkon-connection-404';
import { ignoreCompanyById404Rule } from './ignore-rules/ignore-company-by-id-404';
import { ignoreCompanyByTreadIdNotFoundWhenConnectingAccountRule } from './ignore-rules/ignore-company-by-tread-id-not-found-when-connecting-account';
import { ignoreHaulWareConnection404Rule } from './ignore-rules/ignore-haul-ware-connection-404';
import { ignoreJob404Rule } from './ignore-rules/ignore-job-404';
import { ignorePayableOrReceivable404Rule } from './ignore-rules/ignore-payable-or-receivable-404';
import { ignoreSite404Rule } from './ignore-rules/ignore-site-404';
import { ignoreSiteAdditionalGeofences404Rule } from './ignore-rules/ignore-site-additional-geofences-404';
import { ignoreSiteEquipmentStats404Rule } from './ignore-rules/ignore-site-equipment-stats-404';
import { ignoreSiteMissingCoordinates } from './ignore-rules/ignore-site-missing-coordinates';
import { ignoreUserUpdate404Rule } from './ignore-rules/ignore-user-update-404';
import { ignoreVetaCenterConnection404Rule } from './ignore-rules/ignore-veta-center-connection-404';

const CHECK_IGNORE_RULES_TAG = 'check_ignore_rules';
const DELETE_NOT_FOUND_STATUS_CODE = 404;
// HTTP client (4xx) range — used to scope method+path fingerprinting to client
// errors so 5xx server errors are not merged into the same Sentry issue.
const HTTP_CLIENT_ERROR_MIN_STATUS_CODE = 400;
const HTTP_SERVER_ERROR_MIN_STATUS_CODE = 500;
const DIGITS_PATH_SEGMENT_REGEX = /^\d+$/;
const HTTP_CLIENT_ERROR_FINGERPRINT_PREFIX = 'http-client-error';
const HTTP_CLIENT_MECHANISM_TYPE = 'http.client';
const RELATIVE_URL_PARSE_BASE = 'https://tread.local';
const UUID_PATH_SEGMENT_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Avoid errors with the following HTTP status codes from flowing to Sentry
const SENTRY_OMITTED_HTTP_STATUS_CODES = [401];

// Expected statuses, nothing to investigate: 401/403 (UI-gated) and 422 — the
// channel horizon-api uses for validation failures the FE does not duplicate.
const NON_ACTIONABLE_API_STATUS_CODES = [401, 403, 422];

// Worth recording as an actionable error. Skips the expected statuses above and
// a DELETE that 404s (record already gone). This is Sentry's complement, not its
// mirror: horizon-api reports only a subset, so a 404 RecordNotFound and a 409
// AASM::InvalidTransition reach no Sentry issue — capturing those is the point.
export const isActionableApiError = (status: number, method?: string) => {
  if (NON_ACTIONABLE_API_STATUS_CODES.includes(status)) {
    return false;
  }

  const isDeleteNotFound =
    method?.toUpperCase() === 'DELETE' && status === DELETE_NOT_FOUND_STATUS_CODE;

  return !isDeleteNotFound;
};

// Errors `connectionModule`'s response interceptor has already reported to Sentry, so
// `handleRequestError` can tell that from an identically shaped error that never
// went through the interceptor. Weak, so entries go away with the errors.
const errorsCapturedByResponseInterceptor = new WeakSet<object>();

// `handleRequestError` rethrows each API failure as a new error, and Sentry only
// skips an error object it has already captured. Only the rethrows go here, never
// the originals above: `sentryBeforeSend` drops every event for an error in this set.
const rethrowsOfReportedErrors = new WeakSet<object>();

export function recordErrorCapturedByResponseInterceptor(error: object) {
  errorsCapturedByResponseInterceptor.add(error);
}

export function isErrorCapturedByResponseInterceptor(error: unknown) {
  return isObject(error) && errorsCapturedByResponseInterceptor.has(error);
}

export function recordRethrowOfReportedError(rethrownError: unknown) {
  if (isObject(rethrownError)) {
    rethrowsOfReportedErrors.add(rethrownError);
  }
}

export function isRethrowOfReportedError(error: unknown) {
  return isObject(error) && rethrowsOfReportedErrors.has(error);
}

// Ignore rules to be used with `withSentryIgnoreRules`, defined as an "enum" for type safety
export enum SentryIgnoreRule {
  IGNORE_COMMAND_ALKON_CONNECTION_404 = 'ignore_command_alkon_connection_404',
  IGNORE_COMPANY_BY_ID_404 = 'ignore_company_by_id_404',
  IGNORE_COMPANY_BY_TREAD_ID_NOT_FOUND_WHEN_CONNECTING_ACCOUNT = 'ignore_company_by_tread_id_not_found_when_connecting_account',
  IGNORE_HAUL_WARE_CONNECTION_404 = 'ignore_haul_ware_connection_404',
  IGNORE_JOB_404 = 'ignore_job_404',
  IGNORE_PAYABLE_OR_RECEIVABLE_404 = 'ignore_payable_or_receivable_404',
  IGNORE_SITE_404 = 'ignore_site_404',
  IGNORE_SITE_ADDITIONAL_GEOFENCES_404 = 'ignore_site_additional_geofences_404',
  IGNORE_SITE_EQUIPMENT_STATS_404 = 'ignore_site_equipment_stats_404',
  IGNORE_SITE_MISSING_COORDINATES = 'ignore_site_missing_coordinates',
  IGNORE_USER_UPDATE_404 = 'ignore_user_update_404',
  IGNORE_VETA_CENTER_CONNECTION_404 = 'ignore_veta_center_connection_404',
}

/**
 * A map that associates Sentry ignore rule identifiers with their corresponding validation functions.
 *
 * Each entry in this map contains:
 * - **Key**: A `SentryIgnoreRule` enum value that uniquely identifies the ignore rule
 * - **Value**: A function that takes a Sentry `ErrorEvent` and returns a boolean indicating
 *   whether the error should be ignored (true) or reported to Sentry (false)
 *
 * The validation functions receive the full Sentry error event context, allowing them to inspect
 * various properties like HTTP status codes, request URLs, error messages, and other contextual
 * information to make intelligent decisions about error filtering.
 *
 * @see {@link SentryIgnoreRule} - The enum containing available ignore rule identifiers
 * @see {@link sentryIgnoreRules} - The default implementation of this type
 */
type SentryIgnoreRuleset = Map<SentryIgnoreRule, (event: Sentry.ErrorEvent) => boolean>;

// Actual definition of the ignore ruleset
const sentryIgnoreRules: SentryIgnoreRuleset = new Map([
  [
    SentryIgnoreRule.IGNORE_COMMAND_ALKON_CONNECTION_404,
    ignoreCommandAlkonConnection404Rule,
  ],
  [SentryIgnoreRule.IGNORE_COMPANY_BY_ID_404, ignoreCompanyById404Rule],
  [
    SentryIgnoreRule.IGNORE_COMPANY_BY_TREAD_ID_NOT_FOUND_WHEN_CONNECTING_ACCOUNT,
    ignoreCompanyByTreadIdNotFoundWhenConnectingAccountRule,
  ],
  [SentryIgnoreRule.IGNORE_HAUL_WARE_CONNECTION_404, ignoreHaulWareConnection404Rule],
  [SentryIgnoreRule.IGNORE_JOB_404, ignoreJob404Rule],
  [SentryIgnoreRule.IGNORE_PAYABLE_OR_RECEIVABLE_404, ignorePayableOrReceivable404Rule],
  [SentryIgnoreRule.IGNORE_SITE_404, ignoreSite404Rule],
  [
    SentryIgnoreRule.IGNORE_SITE_ADDITIONAL_GEOFENCES_404,
    ignoreSiteAdditionalGeofences404Rule,
  ],
  [SentryIgnoreRule.IGNORE_SITE_EQUIPMENT_STATS_404, ignoreSiteEquipmentStats404Rule],
  [SentryIgnoreRule.IGNORE_SITE_MISSING_COORDINATES, ignoreSiteMissingCoordinates],
  [SentryIgnoreRule.IGNORE_USER_UPDATE_404, ignoreUserUpdate404Rule],
  [SentryIgnoreRule.IGNORE_VETA_CENTER_CONNECTION_404, ignoreVetaCenterConnection404Rule],
]);

type SentryExtraTags = Record<string, Parameters<typeof Sentry.setTag>[1]>;

// `connectionModule`'s interceptor capture describes its failed request the same
// way these synthetic events do, so only the mechanism tells the two apart.
function isHttpClientIntegrationEvent(event: Sentry.ErrorEvent) {
  return event.exception?.values?.[0]?.mechanism?.type === HTTP_CLIENT_MECHANISM_TYPE;
}

// Origin, not path: a path rule would conflate `api.tread-horizon.com/v1/x` with
// any other host serving `/v1/x`. An unparseable url — including the empty
// `xhr.responseURL` left by a request that got no response — returns false, which
// keeps the event.
export function isApiOriginUrl(url: string, apiBaseUrl: string) {
  try {
    return new URL(url).origin === new URL(apiBaseUrl).origin;
  } catch {
    return false;
  }
}

function getHttpClientResponseStatusCode(event: Sentry.ErrorEvent) {
  const statusCodeCandidate = event.contexts?.response?.status_code;

  if (statusCodeCandidate === undefined || statusCodeCandidate === null) {
    return undefined;
  }

  const statusCode = Number(statusCodeCandidate);

  if (!Number.isFinite(statusCode)) {
    return undefined;
  }

  return statusCode;
}

// Collapses id-like path segments to `:id`. Used both for the Sentry
// http-client-error fingerprint below and by connectionModule's `emitApiError`,
// so the `route` on an api_error event is the exact string Sentry fingerprinted.
export function normalizeHttpClientErrorPath(url: string) {
  try {
    const pathname = new URL(url, RELATIVE_URL_PARSE_BASE).pathname;

    return pathname
      .split('/')
      .map((pathSegment) => {
        const pathSegmentIsIdentifier =
          UUID_PATH_SEGMENT_REGEX.test(pathSegment) ||
          DIGITS_PATH_SEGMENT_REGEX.test(pathSegment);

        return pathSegmentIsIdentifier ? ':id' : pathSegment;
      })
      .join('/');
  } catch {
    return undefined;
  }
}

/**
 * Executes a callback function within a Sentry scope that applies ignore rules and optional tags.
 *
 * This function provides a clean way to wrap code execution with Sentry error filtering capabilities.
 * It creates an isolated Sentry scope where specific ignore rules are applied to control which errors
 * get reported to Sentry, while also allowing you to add contextual tags for better error tracking.
 *
 * ## Key Features
 * - **Error Filtering**: Apply one or more ignore rules to suppress specific types of errors
 * - **Scope Isolation**: Each call creates a new Sentry scope, preventing tag/rule pollution
 * - **Custom Tagging**: Add contextual tags to help categorize and filter errors in Sentry
 * - **Type Safety**: Full TypeScript support with proper type inference
 *
 * ## How It Works
 * 1. Creates a new Sentry scope for the callback execution
 * 2. Sets a special tag (`check_ignore_rules`) containing the rule names to be evaluated
 * 3. Applies any additional custom tags provided
 * 4. Executes the callback with access to the configured scope
 * 5. The `shouldOmitSentryReporting` function later uses these tags to determine if errors should be suppressed
 *
 * @template T The return type of the callback function
 * @param rules - A single rule name or array of rule names from the `SentryIgnoreRule` enum.
 *                These rules determine which errors should be ignored by Sentry during the callback execution.
 *                Each rule is evaluated by the corresponding function in `sentryIgnoreRules`.
 * @param callback - The function to execute within the Sentry scope. Receives the Sentry scope
 *                   as its first argument, allowing for additional scope configuration like setting
 *                   error levels, user context, or other Sentry-specific metadata.
 * @param extraTags - Optional object containing key-value pairs to be set as Sentry tags
 *                    for better error categorization and filtering in the Sentry dashboard.
 *                    These tags help identify patterns and provide context for debugging.
 *
 * @returns The return value of the executed callback function, maintaining full type safety
 *
 * @example
 * ```typescript
 * // Basic usage with a single ignore rule
 * const result = withSentryIgnoreRules(
 *   SentryIgnoreRule.IGNORE_PAYABLE_OR_RECEIVABLE_404,
 *   (scope) => {
 *     // Your code that might throw errors
 *     return someRiskyOperation();
 *   }
 * );
 *
 * // Advanced usage with multiple rules and custom tags
 * const result = withSentryIgnoreRules(
 *   [
 *     SentryIgnoreRule.IGNORE_PAYABLE_OR_RECEIVABLE_404,
 *     // Add more rules as they become available
 *   ],
 *   (scope) => {
 *     // Additional scope configuration
 *     scope.setLevel('warning');
 *     scope.setContext('operation', { type: 'payment_processing' });
 *     return anotherRiskyOperation();
 *   },
 *   {
 *     feature: 'payment_processing',
 *     user_type: 'premium',
 *     environment: 'production'
 *   }
 * );
 *
 * // Usage in async operations
 * const asyncResult = await withSentryIgnoreRules(
 *   SentryIgnoreRule.IGNORE_PAYABLE_OR_RECEIVABLE_404,
 *   async (scope) => {
 *     scope.setTag('operation', 'async_payment');
 *     return await processPaymentAsync();
 *   }
 * );
 * ```
 *
 * @see {@link sentryIgnoreRules} - The map containing available ignore rule implementations
 * @see {@link SentryIgnoreRule} - The enum containing available ignore rule identifiers
 * @see {@link shouldOmitSentryReporting} - The function that evaluates ignore rules during error reporting
 *
 * NOTE: the tag this sets only reaches events captured INSIDE the callback before
 * it awaits. Sentry pops this scope when the callback returns its promise, so a
 * rule passed here does not suppress anything reported off the response — not the
 * `httpClientIntegration` event and not `connectionModule`'s interceptor capture.
 * Rules that need to hold for those go in `mandatoryRulesToCheck` instead.
 */
export function withSentryIgnoreRules<T>(
  rules: SentryIgnoreRule | SentryIgnoreRule[],
  callback: (scope: Sentry.Scope) => T,
  extraTags?: SentryExtraTags,
): T {
  return Sentry.withScope((scope) => {
    const rulesToCheck = Array.isArray(rules) ? rules : [rules];

    scope.setTag(CHECK_IGNORE_RULES_TAG, rulesToCheck.join(','));
    Object.entries(extraTags ?? {}).forEach(([key, value]) => scope.setTag(key, value));

    return callback(scope);
  });
}

/**
 * Determines whether a Sentry error event should be omitted from reporting based on HTTP status codes and configured ignore rules.
 *
 * This function is used as a Sentry `beforeSend` callback to filter out errors that should not be
 * reported to Sentry. It performs two levels of filtering:
 *
 * 1. **HTTP Status Code Filtering**: Automatically omits errors with specific HTTP status codes
 *    that are considered expected behavior and not actionable errors.
 *
 * 2. **Ignore Rules Evaluation**: Examines the error event's tags to identify which ignore rules
 *    should be applied, then evaluates each rule to determine if the error should be suppressed.
 *
 * The function works by:
 * 1. Checking if the error has an HTTP status code that should be omitted (from `SENTRY_OMITTED_HTTP_STATUS_CODES`)
 * 2. If not omitted by status code, extracting the list of ignore rules from the event's tags (set by `withSentryIgnoreRules`)
 * 3. Iterating through each rule and executing its validation function
 * 4. Returning `true` if the error should be omitted (either by status code or any ignore rule), `false` otherwise
 *
 * @param event - The Sentry error event to evaluate for potential omission
 * @returns `true` if the error should be omitted from Sentry reporting, `false` otherwise
 *
 * @see {@link withSentryIgnoreRules} - Function that sets up ignore rules for error tracking
 * @see {@link sentryIgnoreRules} - The map containing available ignore rule implementations
 * @see {@link SentryIgnoreRule} - The enum containing available ignore rule identifiers
 * @see {@link SENTRY_OMITTED_HTTP_STATUS_CODES} - Array of HTTP status codes that are automatically omitted
 */
export function shouldOmitSentryReporting(
  event: Sentry.ErrorEvent,
  hint?: Sentry.EventHint,
) {
  // Axios errors with no `response` never received an HTTP reply: the request was
  // canceled (navigation / unmount / debounce — `ERR_CANCELED`) or hit a client-side
  // network drop or timeout (`ERR_NETWORK` / `ECONNABORTED`). These mirror the
  // `isClientNetworkError` predicate in `connectionModule.handleRequestError`, which
  // already skips its explicit `Sentry.captureException` for them. They still reach
  // here because `handleRequestError` re-throws unconditionally and fire-and-forget
  // callers that don't catch it turn that throw into an unhandled rejection captured
  // by Sentry's global `onunhandledrejection` handler.
  // Dropping them here makes the suppression hold on that path too (TRE-15166).
  const originalException = hint?.originalException;
  if (isAxiosError(originalException) && !originalException.response) {
    return true;
  }

  // A DELETE that 404s means the record is already gone — never an actionable bug.
  const httpClientStatusCode = getHttpClientResponseStatusCode(event);
  const isDeleteNotFoundError =
    event.request?.method?.toUpperCase() === 'DELETE' &&
    httpClientStatusCode === DELETE_NOT_FOUND_STATUS_CODE;

  if (isDeleteNotFoundError) {
    return true;
  }

  const errorStatusCode = Number(event.extra?.error_status ?? null);
  const isOmittedStatusCode = SENTRY_OMITTED_HTTP_STATUS_CODES.includes(errorStatusCode);

  if (isOmittedStatusCode) {
    return true;
  }

  // The following rules are mandatory to check, even if they are not set in the event
  // tags. This protects us when browser-level request instrumentation captures an event
  // under a different active scope than the one where the request was started.
  const mandatoryRulesToCheck = [
    SentryIgnoreRule.IGNORE_COMMAND_ALKON_CONNECTION_404,
    SentryIgnoreRule.IGNORE_HAUL_WARE_CONNECTION_404,
    SentryIgnoreRule.IGNORE_JOB_404,
    SentryIgnoreRule.IGNORE_VETA_CENTER_CONNECTION_404,
    SentryIgnoreRule.IGNORE_SITE_MISSING_COORDINATES,
    SentryIgnoreRule.IGNORE_SITE_404,
    SentryIgnoreRule.IGNORE_SITE_ADDITIONAL_GEOFENCES_404,
  ];

  const tagValue = (event.tags?.[CHECK_IGNORE_RULES_TAG] ?? '') as string;
  const rulesToCheck = tagValue.split(',') as SentryIgnoreRule[];

  const shouldOmit = rulesToCheck.concat(mandatoryRulesToCheck).some((ruleName) => {
    const rule = sentryIgnoreRules.get(ruleName);

    if (rule) {
      return rule(event);
    }

    return false;
  });

  return shouldOmit;
}

/**
 * `beforeSend` callback for `Sentry.init`. Extracted from `main.tsx` (the app
 * bootstrap, which is excluded from coverage) so the suppression decision stays
 * unit-tested. Returns `null` to drop the event, or the event to report it.
 */
export function sentryBeforeSend(event: Sentry.ErrorEvent, hint?: Sentry.EventHint) {
  if (shouldOmitSentryReporting(event, hint)) {
    return null;
  }

  if (isRethrowOfReportedError(hint?.originalException)) {
    return null;
  }

  const isHttpClientEvent = isHttpClientIntegrationEvent(event);
  const httpClientStatusCode = getHttpClientResponseStatusCode(event);
  const requestMethod = event.request?.method;
  const requestUrl = event.request?.url;

  // `connectionModule`'s response interceptor already captured this failure, so
  // this event duplicates it. Gated on the API origin because the raw `fetch` calls
  // in `useDirectUploads` and `imageToBase64` never reach the axios client, and on
  // `isActionableApiError` so a status the interceptor skips keeps this event as its
  // only record rather than being dropped by both paths.
  const isDuplicateApiFailure =
    isHttpClientEvent &&
    isApiOriginUrl(requestUrl ?? '', API_BASE_URL) &&
    httpClientStatusCode !== undefined &&
    isActionableApiError(httpClientStatusCode, requestMethod);

  if (isDuplicateApiFailure) {
    return null;
  }

  // Scoped to 4xx so 5xx server errors keep their message-based grouping rather
  // than collapsing with client errors on the same endpoint.
  const isClientError =
    isHttpClientEvent &&
    httpClientStatusCode !== undefined &&
    httpClientStatusCode >= HTTP_CLIENT_ERROR_MIN_STATUS_CODE &&
    httpClientStatusCode < HTTP_SERVER_ERROR_MIN_STATUS_CODE;

  if (requestMethod && requestUrl && isClientError) {
    const normalizedPath = normalizeHttpClientErrorPath(requestUrl);

    if (normalizedPath) {
      event.fingerprint = [
        HTTP_CLIENT_ERROR_FINGERPRINT_PREFIX,
        requestMethod.toUpperCase(),
        normalizedPath,
      ];
    }
  }

  return event;
}
